import { getPrices, persistCart } from '@/api/calculation'
import { mapActions, mapGetters } from 'vuex'
import { getBaseProductVariantId } from '@/utils/helpers'
import { format } from 'date-fns/format'
import axios from 'axios'

export default {

  data: () => ({
    persistCartAbortController: new AbortController(),
    loadPricesAbortController: new AbortController(),
  }),

  mounted() {
    this.setIsLoadingCart(false)
    this.setIsLoadingPrices(false)
  },

  methods: {
    /**
     * Manages loading state for cart operations
     * @param {string} loadingType - 'cart' or 'prices'
     * @param {boolean} isLoading - Whether to set loading state
     */
    _setLoadingState(loadingType, isLoading) {
      if (loadingType === 'cart') {
        this.setIsLoadingCart(isLoading)
      } else {
        this.setIsLoadingPrices(isLoading)
      }
    },

    /**
     * Gets current loading state for cart operations
     * @param {string} loadingType - 'cart' or 'prices'
     * @returns {boolean}
     */
    _getLoadingState(loadingType) {
      return loadingType === 'cart' ? this.isLoadingCart : this.isLoadingPrices
    },

    /**
     * Handles abort controller management for requests
     * @param {string} loadingType - 'cart' or 'prices'
     * @returns {AbortController}
     */
    _handleAbortController(loadingType) {
      const controller = loadingType === 'cart' ? this.persistCartAbortController : this.loadPricesAbortController

      if (this._getLoadingState(loadingType)) {
        this.$Progress.start()
        controller.abort()

        const newController = new AbortController()
        if (loadingType === 'cart') {
          this.persistCartAbortController = newController
        } else {
          this.loadPricesAbortController = newController
        }
        return newController
      }

      return controller
    },

    /**
     * Common request handler for cart operations
     * @param {Function} apiCall - The API function to call
     * @param {string} loadingType - 'cart' or 'prices' for loading state management
     * @param {Object} options - Additional options
     * @returns {Promise<ApiFormattedCart>}
     */
    async _handleCartRequest(apiCall, loadingType, options = {}) {
      this.$Progress.start()

      if (!this.cart) {
        this.$Progress.fail()
        throw new Error('Cart is empty')
      }

      const abortController = this._handleAbortController(loadingType)
      this._setLoadingState(loadingType, true)

      try {
        const result = await apiCall(Object.freeze(this.cart), abortController.signal)
        const [ updatedCart, currentManufacturingJobUuid, currentManufacturingJobGroupId ] = result

        this._setLoadingState(loadingType, false)
        if (loadingType === 'cart') {
          this.setIsLoadingPrices(false)
        }

        if (updatedCart) {
          if (options.preserveManufacturingJobs) {
            this.setCartPrices(updatedCart)
          } else {
            this.setCart(updatedCart)
          }
        }

        if (!options.preserveManufacturingJobs && currentManufacturingJobUuid !== this.currentManufacturingJobUuid) {
          this.setCurrentManufacturingJobUuid(currentManufacturingJobUuid)
          this.setCurrentManufacturingJobGroupId(currentManufacturingJobGroupId)
        }

        this.$Progress.finish()
        return updatedCart
      } catch (error) {
        if (!axios.isCancel(error)) {
          this.$Progress.fail()
          this._setLoadingState(loadingType, false)
          throw error
        }
      }
    },

    /**
     * Updates and saves the cart to the database
     * @returns {Promise<ApiFormattedCart>}
     */
    async updateCart() {
      const apiCall = (cart, signal) => persistCart(
        cart,
        true,
        false,
        this.currentManufacturingJobUuid,
        signal
      )

      return this._handleCartRequest(
        apiCall,
        'cart'
      )
    },

    /**
     * Creates dummy products for pricing calculations
     * @param {ApiFormattedCart} cart - The current cart
     * @param {ApiFormattedProduct} product - The product to create dummies for
     * @param {number} totalProductCount - Total quantity for the dummy product
     * @returns {Array<{variants: ApiFormattedVariant[], base_product_id: number, created_at: string}>}
     */
    createDummyProducts(cart, product, totalProductCount) {
      const baseProduct = this.baseProducts.find(({ id }) => id === product.base_product_id)
      const firstColor = baseProduct.variants.data[0]
      const firstSize = firstColor.size_variants.data[0]

      return [ {
        base_product_id: product.base_product_id,
        created_at: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        variants: [ /** @type ApiFormattedVariant */{
          baseproduct_variant_id: getBaseProductVariantId(product.base_product_id, firstColor.id, firstSize.size_id),
          amount: totalProductCount,
          color_id: firstColor.id,
          size_id: firstSize.size_id,
        } ]
      } ]
    },

    /**
     * Calculates updated prices without persisting to database
     * @param {boolean} generateThumbnail - Whether to generate product thumbnails
     * @param {boolean} preserveManufacturingJobs - Whether to preserve existing manufacturing jobs
     * @returns {Promise<ApiFormattedCart>}
     */
    async calculatePrices(generateThumbnail = true, preserveManufacturingJobs = false) {
      const apiCall = (cart, signal) => getPrices(
        cart,
        generateThumbnail,
        this.currentManufacturingJobUuid,
        signal
      )

      return this._handleCartRequest(
        apiCall,
        'prices',
        { preserveManufacturingJobs }
      )
    },

    /**
     * @deprecated Use calculatePrices instead
     * @param {boolean} generateThumbnail
     * @param {boolean} preserveManufacturingJobs
     * @returns {Promise<ApiFormattedCart>}
     */
    async updatePrice(generateThumbnail = true, preserveManufacturingJobs = false) {
      return this.calculatePrices(generateThumbnail, preserveManufacturingJobs)
    },

    /**
     * Calculates VAT amount from gross price
     * @param {number} grossPrice - The gross price including VAT
     * @param {number} vatRate - VAT rate as percentage (e.g., 19 for 19%)
     * @returns {number} VAT amount rounded to nearest cent
     */
    _calculateVatFromGross(grossPrice, vatRate) {
      if (!grossPrice || !vatRate) return 0
      const vatDecimal = vatRate / 100
      return Math.round(grossPrice / (1 + vatDecimal) * vatDecimal)
    },

    /**
     * Gets manufacturing jobs for a specific group
     * @param {ApiFormattedManufacturingJobGroupId} manufacturingJobGroupId
     * @returns {ApiFormattedManufacturingJob[]} Manufacturing jobs in the group
     */
    _getJobsForGroup(manufacturingJobGroupId) {
      if (!this.cart || !this.cart.manufacturing_jobs || !this.cart.manufacturing_jobs.data) return []

      return this.cart.manufacturing_jobs.data.filter(
        job => job.group_id === manufacturingJobGroupId
      )
    },

    /**
     * Calculates pricing information for a specific manufacturing job group
     * @param {ApiFormattedManufacturingJobGroupId} manufacturingJobGroupId - The group ID to calculate prices for
     * @returns {ManufacturingJobPriceItem | null} Price breakdown including totals, VAT, and discounts
     */
    priceForGroup(manufacturingJobGroupId) {
      if (!this.cart) return null

      const vatRate = parseFloat(this.cart.vat_rate) || 0
      const jobsInGroup = this._getJobsForGroup(manufacturingJobGroupId)

      if (jobsInGroup.length === 0) {
        return {
          total: 0,
          discount: 0,
          vat_rate: vatRate,
          vat: 0,
          products: {}
        }
      }

      const totals = jobsInGroup.reduce((acc, job) => {
        const jobTotal = job.sum_item_price_gross || 0
        const jobDiscount = job.discount || 0
        const jobVat = this._calculateVatFromGross(jobTotal, vatRate)

        return {
          total: acc.total + jobTotal,
          discount: acc.discount + jobDiscount,
          vat: acc.vat + jobVat
        }
      }, { total: 0, discount: 0, vat: 0 })

      return {
        total: totals.total,
        discount: totals.discount,
        vat_rate: vatRate,
        vat: totals.vat,
        products: {}
      }
    },
    ...mapActions([
      'setCart',
      'setCartPrices',
      'setIsLoadingCart',
      'setIsLoadingPrices',
      'setCurrentManufacturingJobUuid',
      'setCurrentManufacturingJobGroupId',
    ])
  },

  computed: {
    ...mapGetters([
      'baseProducts',
      'isLoadingCart',
      'isLoadingPrices',
      'currentManufacturingJobUuid',
      'cart',
    ])
  }

}
